import { Injectable } from "@angular/core";
import createAuth0Client from "@auth0/auth0-spa-js";
import Auth0Client from "@auth0/auth0-spa-js/dist/typings/Auth0Client";
import {
  from,
  of,
  Observable,
  BehaviorSubject,
  combineLatest,
  throwError,
} from "rxjs";
import { tap, catchError, concatMap, shareReplay } from "rxjs/operators";
import { Router } from "@angular/router";
import { HomeService } from "./home/home.service";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { EmployeeService } from "./employee/employee.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private baseURL = environment.baseUrl;
  private contractorInfo;
  private employeeID;
  public role = '';
  public hvacContractor = false;
  public employeetype = '';

  private employeetypeSubject$ = new BehaviorSubject<string>('');
  employeetype$ = this.employeetypeSubject$.asObservable();

  


  // Create an observable of Auth0 instance of client
  auth0Client$ = (
    from(
      createAuth0Client({
        domain: "ppttrial.auth0.com",
        client_id: "k57s2skSiy9dh8t6o7xPQ479GZC1IohS",
        redirect_uri: `${window.location.origin}`,
        cacheLocation: 'localstorage'
      })
    ) as Observable<Auth0Client>
  ).pipe(
    shareReplay(1), // Every subscription receives the same shared value
    catchError((err) => throwError(err))
  );
  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap((res) => (this.loggedIn = res))
  );
  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
  );
  // Create subject and public observable of user profile data
  public userProfileSubject$ = new BehaviorSubject<any>(null);
  //private homeService = new HomeService;

  userProfile$ = this.userProfileSubject$.asObservable();
  // Create a local property for login status
  loggedIn: boolean = null;

  constructor(private router: Router, private http: HttpClient, private employeeService: EmployeeService) {
    // On initial load, check authentication state with authorization server
    // Set up local auth streams if user is already authenticated
    this.localAuthSetup();
    // Handle redirect from Auth0 login
    this.handleAuthCallback();
  }

  // When calling, options can be passed if desired
  // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
  getUser$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser(options))),
      tap({next :(user) => {
        if(user){
          let contractorInfo = {
            contractor_id: user['https://rhv2.com.metadata'].contractor_id,
            designer_id: user['https://rhv2.com.metadata'].designer_id
          }
          this.employeeID = user['https://rhv2.com.metadata'].id;

          this.employeeService.getOneEmployee(this.employeeID).subscribe(
            res => {
              this.setEmployeeType(res.employeetype.trim());
            },
            err => {
              this.setEmployeeType('Employee');
            }
          );

          this.setContractorInfo(contractorInfo);
        this.userProfileSubject$.next(user);

        this.role = user['https://rhv2.com.metadata'].role;

        }
      }, error: error => {
      }, complete: () => {
      }
    })
    );
  }

  /**
   * Get Contractor Info
   */
  getContractorInfo() {
    return this.contractorInfo;
  }

  getEmployeeId() {
    return this.employeeID;
  }

  getEmployeeType() {
    return this.employeetype;
  }

  getEmployeeName(){
    return this.userProfileSubject$.getValue()?.name || '';
  }

  /**
   * Set Contractor Info
   * @param contractorInfo
   */
  setContractorInfo(contractorInfo) {
    this.contractorInfo = contractorInfo
  }

  private localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    checkAuth$.subscribe();
  }

  login(redirectPath: string = "/home") {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        redirect_uri: `${window.location.origin}`,
        appState: { target: redirectPath },
      });
    });
  }

  setEmployeeType(type: string) {
    this.employeetype = type;
    this.employeetypeSubject$.next(type); // Emit the updated value
  }


  
  private handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes("code=") && params.includes("state=")) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap((cbRes) => {
          // Get and set target redirect route from callback results
          targetRoute =
            cbRes.appState && cbRes.appState.target
              ? cbRes.appState.target
              : "/";
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([this.getUser$(), this.isAuthenticated$]);
        })
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        // Redirect to target route after callback processing

        //this.router.navigate([targetRoute]);

        if (loggedIn) {
          targetRoute = user['https://rhv2.com.metadata'].role === 'Builder' ? '/estar' : '/home';
          this.router.navigate([targetRoute]);
        } else {
          this.router.navigate(['/login']);
        }


        let SEPARATOR = ": ";

        let now = new Date();

        let date = "[" + now.toLocaleString() + "]";

        let logEntry =
          "[info]" +
          SEPARATOR +
          date +
          SEPARATOR +
          user.nickname.toString() +
          " logged in.";
        this.http
          .post(
            this.baseURL + "/rhAPI/log/" + 1,
            { message: logEntry },
            { observe: "body" }
          )
          .subscribe(
            (res) => {

            },
            (err) => {

            }
          );
      });
    }
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        client_id: "k57s2skSiy9dh8t6o7xPQ479GZC1IohS",
        returnTo: `${window.location.origin}`,
      });
    });
  }
}
